import PropTypes from "prop-types";

/**
 * RealisticAHU – High-fidelity 3D Air Handling Unit component.
 * Features:
 * - Bright airflow wind streams flowing in the intake (left) and discharging out the supply (right)
 * - Flow speed and fan spin speed scale dynamically with live value (Airflow / CFM / m3/h)
 * - Internal centrifugal supply fan rotating in inspection window (panel 2)
 * - Second panel drive motor belt wheel rotating
 * - 3 Analog pressure/temperature dial gauges with live moving needles
 * - Control station pilot LED (Green when running, Red when stopped)
 */
const RealisticAHU = ({
  liveValue,
  isRunning,
  isOffline,
  speedScale = 1,
  ratio = 1,
  opacity = 1,
  width = "100%",
  height = "100%",
}) => {
  const val = typeof liveValue === "number" ? liveValue : (isRunning && !isOffline ? 50 : 0);
  const effectiveSpeed = Math.max(0.3, speedScale || 1);
  const spinDuration = isRunning && !isOffline ? `${Math.max(0.25, 1.0 / effectiveSpeed)}s` : "0s";
  const flowDuration = isRunning && !isOffline ? `${Math.max(0.4, 1.4 / effectiveSpeed)}s` : "0s";

  return (
    <div
      className={`scada-realistic-container ${isOffline ? "scada-realistic--offline" : ""}`}
      style={{
        width,
        height,
        opacity,
      }}
    >
      {/* 3D Realistic Base Image */}
      <img
        src="/scada-realistic/AHU.png"
        alt="SCADA Realistic AHU"
        className="scada-realistic-img"
      />

      {/* Dynamic Animated Overlay (SVG synchronized in 676x376 space) */}
      <svg
        className="scada-realistic-overlay"
        viewBox="0 0 676 376"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id="ahuLedGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3.5" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
          <filter id="ahuWindGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>

          <linearGradient id="ahuIntakeWind" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#06b6d4" stopOpacity="1" />
          </linearGradient>
          <linearGradient id="ahuSupplyWind" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#06b6d4" stopOpacity="1" />
            <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.5" />
          </linearGradient>
        </defs>

        {/* ── 1. Bright Intake Wind Entering on Left End ── */}
        {isRunning && !isOffline && (
          <g filter="url(#ahuWindGlow)">
            {/* Stream 1 */}
            <path
              d="M 5 130 Q 30 145 60 140"
              stroke="url(#ahuIntakeWind)"
              strokeWidth="6"
              strokeLinecap="round"
              fill="none"
              style={{ animation: `scada-airflow-traverse ${flowDuration} linear infinite` }}
            />
            {/* Stream 2 */}
            <path
              d="M 12 185 Q 40 180 75 190"
              stroke="url(#ahuIntakeWind)"
              strokeWidth="7"
              strokeLinecap="round"
              fill="none"
              style={{ animation: `scada-airflow-traverse ${flowDuration} linear infinite 0.3s` }}
            />
            {/* Stream 3 */}
            <path
              d="M 5 240 Q 35 235 65 245"
              stroke="url(#ahuIntakeWind)"
              strokeWidth="6"
              strokeLinecap="round"
              fill="none"
              style={{ animation: `scada-airflow-traverse ${flowDuration} linear infinite 0.6s` }}
            />
          </g>
        )}

        {/* ── 2. Bright Supply Wind Exiting on Right End ── */}
        {isRunning && !isOffline && (
          <g filter="url(#ahuWindGlow)">
            {/* Stream 1 */}
            <path
              d="M 610 135 Q 640 130 670 145"
              stroke="url(#ahuSupplyWind)"
              strokeWidth="7"
              strokeLinecap="round"
              fill="none"
              style={{ animation: `scada-airflow-traverse ${flowDuration} linear infinite` }}
            />
            {/* Stream 2 */}
            <path
              d="M 605 190 Q 635 195 672 185"
              stroke="url(#ahuSupplyWind)"
              strokeWidth="7"
              strokeLinecap="round"
              fill="none"
              style={{ animation: `scada-airflow-traverse ${flowDuration} linear infinite 0.4s` }}
            />
            {/* Stream 3 */}
            <path
              d="M 610 245 Q 640 240 670 250"
              stroke="url(#ahuSupplyWind)"
              strokeWidth="6"
              strokeLinecap="round"
              fill="none"
              style={{ animation: `scada-airflow-traverse ${flowDuration} linear infinite 0.7s` }}
            />
          </g>
        )}

        {/* ── 3. Internal Centrifugal Supply Fan (Inspection Window: center (193, 183)) ── */}
        <g
          style={{
            transformOrigin: "193px 183px",
            animation: isRunning && !isOffline ? `scada-spin ${spinDuration} linear infinite` : "none",
          }}
        >
          <circle cx="193" cy="183" r="28" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" fill="none" />
          {[0, 60, 120, 180, 240, 300].map((deg) => (
            <path
              key={deg}
              d="M 193 183 Q 205 163 193 153"
              stroke="#60a5fa"
              strokeWidth="3.5"
              strokeLinecap="round"
              fill="none"
              opacity="0.85"
              transform={`rotate(${deg} 193 183)`}
            />
          ))}
          <circle cx="193" cy="183" r="8" fill="#1e293b" stroke="#e2e8f0" strokeWidth="1.5" />
          <circle cx="193" cy="183" r="3" fill="#38bdf8" />
        </g>

        {/* ── 4. Second Panel Drive Motor Wheel (center (415, 183)) ── */}
        <g
          style={{
            transformOrigin: "415px 183px",
            animation: isRunning && !isOffline ? `scada-spin ${spinDuration} linear infinite` : "none",
          }}
        >
          <circle cx="415" cy="183" r="24" stroke="rgba(56, 189, 248, 0.5)" strokeWidth="2" strokeDasharray="8 6" fill="none" />
          <circle cx="415" cy="183" r="7" fill="#1e293b" stroke="#38bdf8" strokeWidth="1.5" />
          <circle cx="415" cy="183" r="2.5" fill="#ffffff" />
        </g>

        {/* ── 5. 3 Analog Dial Gauges Needles ── */}
        {/* Gauge 1 (Filter: x: 277, y: 113) */}
        <line
          x1="277"
          y1="113"
          x2="283"
          y2="104"
          stroke="#ef4444"
          strokeWidth="1.8"
          strokeLinecap="round"
          style={{
            transformOrigin: "277px 113px",
            animation: isRunning && !isOffline ? "scada-needle-jitter 1.5s infinite" : "none",
          }}
        />
        {/* Gauge 2 (Coil: x: 277, y: 150) */}
        <line
          x1="277"
          y1="150"
          x2="271"
          y2="142"
          stroke="#ef4444"
          strokeWidth="1.8"
          strokeLinecap="round"
          style={{
            transformOrigin: "277px 150px",
            animation: isRunning && !isOffline ? "scada-needle-jitter 1.8s infinite 0.3s" : "none",
          }}
        />
        {/* Gauge 3 (Fan: x: 277, y: 186) */}
        <line
          x1="277"
          y1="186"
          x2="283"
          y2="177"
          stroke="#ef4444"
          strokeWidth="1.8"
          strokeLinecap="round"
          style={{
            transformOrigin: "277px 186px",
            animation: isRunning && !isOffline ? "scada-needle-jitter 1.2s infinite 0.7s" : "none",
          }}
        />

        {/* ── 6. Front Control Station Pilot LED (Green when ON, Red when OFF) ── */}
        <circle
          cx="73"
          cy="58"
          r="5.5"
          fill={isRunning && !isOffline ? "#22c55e" : "#ef4444"}
          stroke={isRunning && !isOffline ? "#86efac" : "#fca5a5"}
          strokeWidth="1.2"
          filter="url(#ahuLedGlow)"
          style={{
            animation: isRunning && !isOffline ? "scada-led-blink 2s infinite ease-in-out" : "none",
          }}
        />
        <circle cx="71.5" cy="56.5" r="1.5" fill="#ffffff" opacity="0.8" />
      </svg>
    </div>
  );
};

RealisticAHU.propTypes = {
  liveValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unit: PropTypes.string,
  isRunning: PropTypes.bool,
  isOffline: PropTypes.bool,
  statusColor: PropTypes.string,
  speedScale: PropTypes.number,
  opacity: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default RealisticAHU;
